import { Alert, Card, Descriptions, Spin, Table, Tag } from 'antd';
import { useEffect, useState } from 'react';
import type {
  GeomechanicsDatasetOverview,
  GeomechanicsOverview,
} from '../../../services/api';
import { geomechanicsAPI, getApiErrorMessage } from '../../../services/api';

const STATUS_COLOR: Record<string, string> = {
  draft: 'default',
  queued: 'blue',
  running: 'processing',
  succeeded: 'green',
  failed: 'red',
  reviewed: 'cyan',
  published: 'purple',
  stale: 'orange',
  cancelling: 'gold',
  cancelled: 'default',
};

export default function OverviewModule({
  projectId,
  backendEnabled,
}: {
  projectId: number;
  backendEnabled: boolean;
}) {
  const [overview, setOverview] = useState<GeomechanicsOverview | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    if (!backendEnabled || !projectId) return;
    setLoading(true);
    try {
      const { data } = await geomechanicsAPI.getOverview(projectId);
      setOverview(data);
      setError('');
    } catch (caught: unknown) {
      setError(getApiErrorMessage(caught, '总览加载失败。'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [backendEnabled, projectId]);

  if (!backendEnabled) {
    return <Alert showIcon type="info" message="后端开关关闭，00 数据总览不可用。" />;
  }
  if (loading && !overview) {
    return <Spin tip="正在汇总项目数据…" />;
  }
  if (error) {
    return <Alert showIcon type="error" message={error} />;
  }
  if (!overview) {
    return <Alert showIcon type="info" message="暂无项目数据。" />;
  }

  const datasetRows = (overview.dataset_type_order ?? [])
    .flatMap((dataType) =>
      (overview.datasets[dataType] ?? []).map((dataset) => ({
        ...dataset,
        data_type: dataType,
        key: `${dataType}-${dataset.id}`,
      })),
    )
    .concat(
      Object.entries(overview.datasets)
        .filter(([dataType]) => !(overview.dataset_type_order ?? []).includes(dataType))
        .flatMap(([dataType, datasets]) =>
          datasets.map((dataset) => ({
            ...dataset,
            data_type: dataType,
            key: `${dataType}-${dataset.id}`,
          })),
        ),
    );

  const runRows = (overview.operation_order ?? [])
    .filter((operation) => overview.runs[operation])
    .map((operation) => ({
      operation,
      ...overview.runs[operation],
      key: operation,
    }));

  return (
    <>
      <Descriptions column={4} bordered size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="数据集类型">
          {Object.keys(overview.datasets).length}
        </Descriptions.Item>
        <Descriptions.Item label="运行总数">
          {overview.status_summary.total_runs}
        </Descriptions.Item>
        <Descriptions.Item label="stale">
          <Tag color="orange">{overview.status_summary.stale}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="运行中">
          <Tag color="processing">{overview.status_summary.running}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="失败">
          <Tag color="red">{overview.status_summary.failed}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="PressureProfile 行">
          {overview.stable_tables.pressure.row_count}
        </Descriptions.Item>
        <Descriptions.Item label="RockMechanics 行">
          {overview.stable_tables.rock_mechanics.row_count}
        </Descriptions.Item>
        <Descriptions.Item label="工作台来源行">
          {overview.stable_tables.pressure.source_run_count
            + overview.stable_tables.rock_mechanics.engineering_source_count
            + overview.stable_tables.rock_mechanics.dynamic_source_count
            + overview.stable_tables.rock_mechanics.stress_source_count}
        </Descriptions.Item>
      </Descriptions>

      <Card size="small" title="八类输入数据状态" style={{ marginBottom: 16 }}>
        <Table<GeomechanicsDatasetOverview & { data_type: string; key: string }>
          size="small"
          pagination={false}
          dataSource={datasetRows}
          locale={{ emptyText: '尚未上传 DataHub 数据集' }}
          columns={[
            { title: '类型', dataIndex: 'data_type', width: 130 },
            { title: '数据集', dataIndex: 'name' },
            { title: '版本', dataIndex: 'version', width: 70 },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (value: string) => <Tag>{value}</Tag>,
            },
            { title: '行数', dataIndex: 'row_count', width: 80 },
            { title: '深度基准', dataIndex: 'depth_reference', width: 90 },
            { title: '质控问题', dataIndex: 'issue_count', width: 80 },
            { title: '质量分', dataIndex: 'quality_score', width: 80 },
            {
              title: 'checksum',
              dataIndex: 'content_checksum',
              width: 110,
              render: (value: string) => value?.slice(0, 12) ?? '-',
            },
          ]}
        />
      </Card>

      <Card size="small" title="各模块最新 Run">
        <Table
          size="small"
          pagination={false}
          dataSource={runRows}
          locale={{ emptyText: '尚无正式运行' }}
          columns={[
            { title: '模块', dataIndex: 'operation', width: 180 },
            {
              title: '状态',
              dataIndex: 'status',
              width: 110,
              render: (value: string) => (
                <Tag color={STATUS_COLOR[value] ?? 'default'}>{value}</Tag>
              ),
            },
            { title: 'Run', dataIndex: 'id', width: 70 },
            { title: '行数', dataIndex: 'row_count', width: 70 },
            { title: '深度基准', dataIndex: 'depth_reference', width: 90 },
            {
              title: '复核',
              dataIndex: 'reviewed',
              width: 70,
              render: (value: boolean) => (value ? <Tag color="green">是</Tag> : <Tag>否</Tag>),
            },
            {
              title: '发布',
              dataIndex: 'published',
              width: 70,
              render: (value: boolean) => (value ? <Tag color="purple">是</Tag> : <Tag>否</Tag>),
            },
            {
              title: '血缘',
              key: 'lineage',
              render: (_, record) => {
                const inputs = record.inputs ?? [];
                const dependencies = record.dependencies ?? [];
                return (
                  <>
                    {inputs.length > 0 && (
                      <Tag>输入 {inputs.length} 数据集</Tag>
                    )}
                    {dependencies.length > 0 && (
                      <Tag>依赖 {dependencies.length} 上游</Tag>
                    )}
                    {inputs.length === 0 && dependencies.length === 0 && <span>-</span>}
                  </>
                );
              },
            },
            {
              title: '校验值',
              dataIndex: 'result_checksum',
              render: (value: string | null) =>
                value ? <code>{value.slice(0, 16)}…</code> : '-',
            },
          ]}
        />
      </Card>
    </>
  );
}
